import type {
  AppNewsDoc,
  CommentaryCommentDoc,
  SolrClientInterface,
} from "@rsc/vv-solr-client";
import { useQuery, type UseQueryReturnType } from "@tanstack/vue-query";
import {
  makeResultAsyncSerializable,
  type SerializableResult,
} from "~/errors/util";
import type { LogFunction } from "~/logging/log";

/**
 * Convenience wrapper for `useQuery` and `SolrClient.getNodes`.
 *
 * @param client The Solr client to use.
 * @param nids The IDs of the nodes to fetch (reactive dependency).
 * @param languageCodes The languages in which to load the nodes (reactive dependency).
 * @param fields The fields to include in the results.
 * @param indexId The Solr index ID, which corresponds to the content type.
 * @param logServerSideError A callback to log errors when running on the server.
 */
export function useNodes<PickFields extends Array<keyof CommentaryCommentDoc>>(
  client: SolrClientInterface,
  nids: Ref<number[]>,
  languageCodes: Ref<string[]>,
  fields: PickFields,
  indexId: "commentary_comments",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<Pick<CommentaryCommentDoc, PickFields[number]>[]>,
  never
>;
export function useNodes<PickFields extends Array<keyof AppNewsDoc>>(
  client: SolrClientInterface,
  nids: Ref<number[]>,
  languageCodes: Ref<string[]>,
  fields: PickFields,
  indexId: "app_news",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<Pick<AppNewsDoc, PickFields[number]>[]>,
  never
>;
export function useNodes<
  DocType extends CommentaryCommentDoc | AppNewsDoc,
  PickFields extends Array<keyof DocType>,
>(
  client: SolrClientInterface,
  nids: Ref<number[]>,
  languageCodes: Ref<string[]>,
  fields: PickFields,
  indexId: "commentary_comments" | "app_news",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<Pick<DocType, PickFields[number]>[]>,
  never
> {
  // Workaround for https://www.phind.com/search?cache=coihygy9tz0s1iv3labolkji
  const _fields = fields as any[];

  return useQuery({
    queryKey: ["nodes", nids, languageCodes, _fields, indexId],
    queryFn: () =>
      makeResultAsyncSerializable(
        () =>
          client
            .getNodes(nids.value, _fields, languageCodes.value, indexId)
            .map((doclist) => doclist.docs),
        logServerSideError,
      ),
  });
}
